'use client'

import type { BigIntish, ChainId, Fetchable } from '@morpho-org/blue-sdk'
import type { InterfaceInputToken } from '@morpho-org/dapp-core'

import { cn } from '@morpho-org/ui-v6/utils'
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'

import { MarketIdentity } from './MarketIdentity'

export interface MarketCellProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Collateral asset token */
  collateralAsset: Fetchable<InterfaceInputToken>
  /** Loan asset token */
  loanAsset: Fetchable<InterfaceInputToken>
  /** Liquidation LTV (18 decimals) */
  lltv: BigIntish | null | undefined
  /** Chain ID */
  chainId?: Fetchable<ChainId>
  /** Maximum length for market name symbols */
  maxLength?: number
  /** Render prop for warnings */
  renderWarnings?: ReactNode
  /** Render prop for pending timelocks */
  renderPendingTimelocks?: ReactNode
  /** Render prop for idle liquidity tooltip content */
  renderIdleTooltip?: ReactNode
  /**
   * Forwarded to MarketIdentity. Defaults to false so auto-layout table cells
   * keep the symbol's intrinsic width; set true inside fixed-layout tables.
   */
  truncateSymbols?: boolean
}

/**
 * MarketCell component - Tailwind replica of dapp-ui-v5 MarketCell
 *
 * Wraps the shared loan-first {@link MarketIdentity} with the table-cell affordances:
 * warnings, peripheral sources and pending timelocks. Supports idle liquidity markets
 * (when collateralAsset is undefined).
 *
 * @example
 * ```tsx
 * // Regular market
 * <MarketCell
 *   collateralAsset={wethToken}
 *   loanAsset={usdcToken}
 *   lltv={800000000000000000n}
 *   renderWarnings={<WarningIcon warnings={warnings} />}
 * />
 *
 * // Idle liquidity market
 * <MarketCell
 *   collateralAsset={undefined}
 *   loanAsset={usdcToken}
 *   lltv={null}
 * />
 * ```
 */
const MarketCell = forwardRef<HTMLDivElement, MarketCellProps>(
  (
    {
      collateralAsset,
      loanAsset,
      lltv,
      chainId,
      maxLength,
      renderWarnings,
      renderPendingTimelocks,
      renderIdleTooltip,
      truncateSymbols = false,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <div ref={ref} className={cn('gap-xs flex items-center', className)} {...props}>
        <MarketIdentity
          loanAsset={loanAsset}
          collateralAsset={collateralAsset}
          lltv={lltv}
          maxLength={maxLength}
          showIdleBadge
          renderIdleTooltip={renderIdleTooltip}
          truncateSymbols={truncateSymbols}
        />

        {/* Warnings */}
        {renderWarnings}

        {/* Pending timelocks */}
        {renderPendingTimelocks}
      </div>
    )
  }
)

MarketCell.displayName = 'MarketCell'

export { MarketCell }
